import type { InputHTMLAttributes } from "react";
import Checkbox, { type CheckboxProps } from "@mui/material/Checkbox";
import SvgIcon, { type SvgIconProps } from "@mui/material/SvgIcon";

const UNCHECKED_COLOR = "#BFBFBF";

type DataGridCheckboxSlotProps = {
  htmlInput?: InputHTMLAttributes<HTMLInputElement>;
};

type ThinCheckboxProps = Omit<CheckboxProps, "slotProps"> & {
  slotProps?: CheckboxProps["slotProps"] | DataGridCheckboxSlotProps;
};

function UncheckedIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 16 16">
      <rect
        x="0.5"
        y="0.5"
        width="15"
        height="15"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />
    </SvgIcon>
  );
}

function CheckedIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 16 16">
      <rect x="0.5" y="0.5" width="15" height="15" rx="2" fill="currentColor" />
      <path
        d="M4 8.1 6.7 10.7 12 5.2"
        fill="none"
        stroke="#fff"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  );
}

function IndeterminateIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 16 16">
      <rect x="0.5" y="0.5" width="15" height="15" rx="2" fill="currentColor" />
      <path
        d="M4 8h8"
        fill="none"
        stroke="#fff"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </SvgIcon>
  );
}

function isDataGridSlotProps(
  slotProps: ThinCheckboxProps["slotProps"],
): slotProps is DataGridCheckboxSlotProps {
  return Boolean(slotProps && "htmlInput" in slotProps);
}

/** 16px checkbox with a 1px light outline, matching Regulations list tables. */
export default function ThinCheckbox({
  slotProps,
  sx,
  ...props
}: ThinCheckboxProps) {
  const htmlInput = isDataGridSlotProps(slotProps)
    ? slotProps.htmlInput
    : undefined;
  const muiSlotProps = isDataGridSlotProps(slotProps) ? undefined : slotProps;

  return (
    <Checkbox
      {...props}
      icon={<UncheckedIcon />}
      checkedIcon={<CheckedIcon />}
      indeterminateIcon={<IndeterminateIcon />}
      disableRipple
      inputProps={htmlInput ?? props.inputProps}
      slotProps={muiSlotProps}
      sx={[
        {
          padding: "4px",
          color: UNCHECKED_COLOR,
          "&:hover": { backgroundColor: "transparent" },
          "& .MuiSvgIcon-root": { fontSize: 16 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
